import { useMemo, useState } from "react";
import { Pencil, Search, Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { api, fetchCollection, formatApiError } from "@/lib/api";
import { formatDate } from "@/utils/format";

export function AdminItemList({ queryKey, endpoint, title, onEdit, getTitle, getMeta, fetchPath, invalidate = [], eyebrow = "Contenido publicado" }) {
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: [queryKey], queryFn: () => fetchCollection(fetchPath || `/${queryKey}`) });
  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/${endpoint}/${id}`)).data,
    onSuccess: () => {
      toast.success("Contenido eliminado");
      [queryKey, "stats", ...invalidate].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return data.filter((item) => `${getTitle(item)} ${getMeta(item)}`.toLowerCase().includes(term));
  }, [data, search, getTitle, getMeta]);
  const visible = filtered.slice(0, 25);

  const remove = (item) => {
    if (window.confirm(`¿Eliminar “${getTitle(item)}”?`)) {
      deleteMutation.mutate(item.id);
    }
  };

  return (
    <div className="mt-10 border-t border-white/10 pt-8" data-testid={`admin-${queryKey}-list`}>
      <div className="mb-5 flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h4 className="mt-2 font-display text-3xl uppercase leading-none text-slate-50">{title}</h4>
          <p className="mt-2 text-sm text-slate-500">{filtered.length} elementos</p>
        </div>
        <label className="relative block md:w-80">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-clay" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar contenido" className="h-11 rounded-none border-white/10 bg-charcoal pl-11 text-slate-100 placeholder:text-slate-600" data-testid={`admin-${queryKey}-search-input`} />
        </label>
      </div>

      {isLoading ? <p className="text-sm text-slate-500">Cargando contenido...</p> : null}

      <div className="grid gap-3">
        {visible.map((item) => (
          <article key={item.id} className="flex flex-col gap-4 border border-white/10 bg-white/[0.025] p-4 md:flex-row md:items-center md:justify-between" data-testid={`admin-${queryKey}-item-${item.id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h5 className="truncate text-base font-bold text-slate-100">{getTitle(item)}</h5>
                {item.source === "google_sheet" ? <span className="border border-clay/25 bg-clay/10 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-clay">Google Sheet</span> : null}
                {item.status === "pending" ? <span className="border border-brass/40 bg-brass/10 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-brass" data-testid={`admin-status-pending-${item.id}`}>Pendiente</span> : null}
                {item.status === "rejected" ? <span className="border border-red-500/30 bg-red-500/10 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-red-200" data-testid={`admin-status-rejected-${item.id}`}>Rechazada</span> : null}
              </div>
              <p className="mt-1 text-sm text-slate-500">{formatDate(item.date)} · {getMeta(item)}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button type="button" onClick={() => onEdit(item)} variant="outline" className="rounded-none border-white/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.18em] hover:border-clay/50 hover:bg-clay/10 hover:text-clay" data-testid={`admin-edit-${queryKey}-${item.id}`}>
                Editar <Pencil className="h-3 w-3" />
              </Button>
              <Button type="button" onClick={() => remove(item)} variant="outline" className="rounded-none border-red-500/25 bg-red-500/5 font-mono text-[10px] uppercase tracking-[0.18em] text-red-200 hover:bg-red-500/20" data-testid={`admin-delete-${queryKey}-${item.id}`}>
                Borrar <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </article>
        ))}
      </div>

      {filtered.length > visible.length ? <p className="mt-4 text-xs text-slate-500" data-testid={`admin-${queryKey}-more-count`}>Mostrando 25 de {filtered.length}. Usa el buscador para localizar el contenido.</p> : null}
    </div>
  );
}
